import type { TranslationTree } from "./types.js";

export const zhCN = {
  app: {
    title: "修仙 IO",
    shortTitle: "修仙",
    runtimeTitle: "修仙 IO Runtime",
  },
  identity: {
    label: "名号",
    placeholder: "名号",
    start: "踏入仙途",
    restart: "再入轮回",
    nameLengthError: "名号长度需要在 1-{max} 个字符之间。",
    scoreSummary: "本场得分 {score}",
    devLevel: "开发初始等级",
    contentLabel: "内容页",
    libraryLink: "藏经阁",
    privacyLink: "隐私政策",
    feedbackLink: "反馈",
    discordLink: "Discord",
    appearance: "换装",
    skinCardLabel: "角色",
    roomCardLabel: "小世界",
    appearanceTitle: "选择你的小修士",
    appearanceSubtitle: "选择修士形象，开启你的修仙之旅",
    appearanceAction: "换装",
    appearanceSelected: "已选",
    appearanceDone: "确定形象",
    skin: "小修士",
    skinStageShape: "入界形态",
    terrain: "脚下秘境",
    terrainGrid: "灵脉棋盘",
    terrainHex: "蜂巢灵田",
    terrainDot: "星砂浮界",
  },
  room: {
    createRoom: "创建小世界",
    createConfirm: "创建小世界",
    joinRoom: "进入",
    joinPrimary: "进入小世界 {code}",
    joinSectionTitle: "用邀请码进入",
    orLabel: "或",
    invitePlaceholder: "邀请码",
    inviteError: "请输入有效的邀请码",
    configMapLabel: "地图",
    configLimitLabel: "人数",
    configBotLabel: "BOT",
    errorMapTooSmall: "当前地图容纳不下这么多人，请调小人数或换大地图",
    errorTooManyBots: "BOT 数量需少于总人数上限",
    waitingTitle: "小世界等待室",
    inviteLabel: "邀请码",
    copyInvite: "复制邀请链接",
    copied: "邀请链接已复制",
    copyFailed: "复制失败，请手动复制",
    startMatch: "开始游戏",
    startNeedsPlayers: "至少需 2 人开始",
    leave: "离开",
    ownerHint: "你是房主，邀请好友后即可开始",
    guestHint: "等待房主开始本局…",
    playersTitle: "小世界成员（{count}/{limit}）",
    ownerTag: "房主",
    botTag: "BOT",
    configSummary: "{mapSize} · 上限 {limit} 人 · {mode}",
    mapSmall: "小型秘境",
    mapMedium: "中型秘境",
    mapLarge: "大型秘境",
    modeSolo: "单人混战",
    modeTeam: "组队对战",
    matchPanelTitle: "本局战况",
    countdownLabel: "剩余 {time}",
    respawnCountdown: "已陨落，{seconds} 秒后再入轮回…",
    standingsTitle: "本局排名",
    resultTitle: "本局结束",
    winnerLabel: "胜者：{name}",
    noWinner: "无人胜出",
    backToEntry: "返回首页",
    spectating: "观战：{name}",
    spectateNone: "暂无可观战目标",
    unavailableTitle: "无法进入小世界",
    endedMessage: "该小世界的游戏已结束，或小世界已不存在。",
    fullMessage: "小世界人数已满。",
    invalidConfigMessage: "小世界配置无效。",
    backHome: "返回首页",
    resetInvite: "重置邀请码",
    kick: "踢出",
    swapTeam: "点击换营",
    rematchButton: "再开一局",
    chatPlaceholder: "说点什么…",
    chatSend: "发送",
    configModeLabel: "玩法",
    winLastSurvivor: "活到最后",
    winTimedScore: "限时最高分",
    winTeamTimedScore: "2 队限时总分",
    teamAzure: "蓝队",
    teamCrimson: "红队",
    teamWinner: "{team} 获胜",
  },
  settings: {
    title: "设置",
    language: "语言",
    sound: "声音",
    fullscreen: "全屏",
    close: "关闭",
    room: "小世界",
    appearance: "换装",
    library: "藏经阁",
    privacy: "隐私政策",
    feedback: "反馈",
    discord: "Discord",
  },
  leaderboard: {
    title: "天榜",
    empty: "等待修士入场",
    mobileToggle: "榜",
    defaultPlayerName: "未命名修士",
    periodLabel: {
      day: "日",
      week: "周",
      month: "月",
    },
    periodTitle: {
      day: "今日前十",
      week: "本周前十",
      month: "本月前十",
    },
  },
  controls: {
    mobileBoost: "疾",
    soundOn: "声音 开",
    soundOff: "声音 关",
    mouseLockOff: "锁鼠 关",
    mouseLockOn: "锁鼠中 Esc取消",
    mouseLockTitleOff: "点击开启鼠标锁定",
    mouseLockTitleOn: "按 Esc 解除鼠标锁定",
  },
  pwa: {
    install: "安装",
    fullscreen: "全屏",
    exitFullscreen: "退出全屏",
    dismissLabel: "关闭提示",
    installTip: "安装到主屏幕，减少浏览器工具栏干扰。",
    manualTip: "添加到主屏幕，体验更接近全屏。",
  },
  locale: {
    label: "语言",
    zhCN: "中文",
    en: "English",
  },
  realm: {
    qi: "练气",
    foundation: "筑基",
    core: "结丹",
    nascent: "元婴",
    spirit: "化神",
    layer: "{realm} {layer} 层",
    qiShort: "练气",
    foundationShort: "筑基",
    coreShort: "结丹",
    nascentShort: "元婴",
    spiritShort: "化神",
    layerCompact: "{realm} {layer} 层",
    ascension: "飞升",
  },
  treasure: {
    qiankun_ruler: {
      name: "乾坤尺",
      shortEffect: "法阵范围大增",
    },
    hunyuan_fan: {
      name: "混元扇",
      shortEffect: "攻击扇面拓宽",
    },
    spirit_gourd: {
      name: "聚灵葫",
      shortEffect: "灵力吸纳提升至 3 倍",
    },
    thunder_sword_box: {
      name: "雷鸣剑匣",
      shortEffect: "飞剑出手更快",
    },
    myriad_sword_token: {
      name: "万剑令",
      shortEffect: "同时斩杀双目标",
    },
    wind_thunder_boots: {
      name: "风雷履",
      shortEffect: "身法增强且疾行不耗气",
    },
    black_tortoise_armor: {
      name: "玄龟甲",
      shortEffect: "免死护主 3 次",
    },
    ascension_pearl: {
      name: "跨界珠",
      shortEffect: "暂未开放",
    },
  },
  runtime: {
    fallbackCultivator: "某位修士",
    status: {
      default: "等待建立到 realtime 服务的连接。",
      entryReady: "请确认名号后进入灵界。",
      bootstrapFailed: "准备名号或建立连接时失败。",
      restartPrompt: "已陨落，请确认名号后重新开始。",
      restartFailed: "重新开始时准备名号失败。",
      connecting: "正在连接灵界通道…",
      websocketOpen: "WebSocket 已打开，正在发送握手…",
      transportError: "连接发生错误，等待服务端关闭或重连。",
      disconnectedRetry: "灵界通道已断开，稍后自动重试。",
      manualDisconnect: "客户端主动关闭连接。",
      reconnecting: "正在重入灵界通道…",
      protocolParseFailed: "服务端协议解析失败。",
      joinedRoom: "已进入小世界 {roomId}，等待世界同步。",
      syncedTick: "同步到第 {tick} 帧世界状态。",
      deadRestart: "已陨落，请确认名号后重新开始。",
      spectatingKiller: "已陨落，点击或按任意键再入轮回。",
      eventReceived: "收到事件：{event}",
      serverErrorReturned: "服务端返回错误消息。",
    },
    error: {
      transport: "网络层出现传输错误。",
      nonTextMessage: "收到非文本协议消息。",
    },
    log: {
      started: "Runtime 已启动，等待连接。",
      connect: "连接 {url}",
      helloSent: "已连接，发送 client_hello。",
      transportError: "连接出现传输错误。",
      close: "连接关闭：{reason}",
      respawnRequested: "已请求重开。",
      nonTextIgnored: "收到非文本协议消息，已忽略。",
      protocolError: "协议错误：{detail}",
      handshake: "握手完成：player={playerId} room={roomId}",
      reconnectIn: "{seconds}s 后尝试重连。",
      serverError: "服务端错误：{code}",
      serviceWorkerFailed: "Service worker 注册失败，继续使用普通网页模式。",
      pwaStorageUnavailable:
        "本地存储不可用，本次主屏幕提示设置仅当前标签页有效。",
      playerKeyStorageUnavailable:
        "本地存储不可用，本次玩家标识仅当前标签页有效。",
      identityStorageUnavailable: "本地存储不可用，本次身份仅当前标签页有效。",
      audioStorageUnavailable: "本地存储不可用，本次声音设置仅当前标签页有效。",
      leaderboardStorageUnavailable:
        "本地存储不可用，本次天榜选择仅当前标签页有效。",
      nameConfirmed: "名号已确认：{name}",
      restartConfirmed: "重新开始：{name}",
      startupFailed: "启动失败：{detail}",
      restartFailed: "重开失败：{detail}",
    },
    event: {
      attackStarted: "起手: {attackerId} -> {targetId}",
      attackHit: "命中: {attackerId} -> {targetId}",
      attackMissed: "落空: {attackerId} -> {targetId}",
      pickup: "吸取: {playerId} / {value} 灵力",
      death: "陨落: {playerId}{killer}{dropped}",
      deathKiller: " / 击杀者 {killerId}",
      deathDropped: " / 掉落 {count} 灵力",
      drop: "爆出: {playerId} / {count} 灵力",
      respawn: "重开: {playerId}",
      ascension: "飞升: {playerId} / {spiritPower} 灵力",
      ascensionStarted: "飞升仪式: {name} / +{scoreGain} 分",
      treasureSpawned: "异宝现世: {kind}",
      treasurePicked: "异宝被取: {kind}",
      treasureGuard: "玄龟甲护主: {playerId} / 剩余 {remainingCharges} 次",
      worldReborn: "飞升归来: {ascenderName} 再入凡尘",
      serverEvent: "服务端事件：{event}",
    },
    roomEvent: {
      playerJoined: "{name} 加入了小世界",
      playerLeft: "{name} 离开了小世界",
      ownerTransferred: "房主已转移给 {ownerName}",
      roomStarted: "房主已开始本局",
      roomClosed: "小世界已关闭",
      matchEnded: "对局结束：胜者 {winnerName}",
      playerKicked: "你已被房主移出小世界",
      inviteReset: "邀请码已重置",
      rematch: "房主开启了新一局",
    },
    treasureNotice: {
      spawnTitle: "异宝现世",
      pickupTitle: "恭喜获得宝物",
      guardTitle: "玄龟甲护主",
      guardSelfBody: "替你挡下一劫，剩余 {remainingCharges} 次",
      guardOtherBody: "挡下飞剑，剩余 {remainingCharges} 次",
      ascensionTitle: "有人飞升",
      ascensionBody: "{name} 天门大开，天地暂息",
      iconText: "飞",
    },
    ascension: {
      selfTitle: "你已飞升",
      otherTitle: "{name} 飞升成功",
      selfSubtitle: "飞升得分 +{scoreGain}，总分 {totalScore}",
      otherSubtitle: "天门已开，天地暂息，共观飞升",
      seal: "飞升",
      countdownUnit: "息",
      rebornSeal: "归尘",
      rebornTitle: "天地复归流转",
      rebornSubtitle: "{name} 已携积分再入凡尘，诸修继续此局。",
      announcementNoScore: "{name} 飞升成功，天门已开。",
      announcementWithScore: "{name} 飞升成功，总分 {totalScore}。",
      worldRebornAnnouncement: "{name} 飞升成功，天地复归流转。",
    },
    hint: {
      mobile: "左下摇杆御风 · 右下按住疾行",
      desktop: "鼠标定向御风 · 左键/Shift 疾行 · M 锁鼠 · Esc 解锁",
    },
    debug: {
      formationLayers: "性能开关：法阵贴图层数 {count}",
      background: "性能开关：背景 {mode}",
      stones: "性能开关：灵石 {mode}",
      minimap: "性能开关：小地图 {mode}",
      playerSprites: "性能开关：角色精灵 {mode}",
      simplified: "简化",
      full: "完整",
      shown: "显示",
      hidden: "隐藏",
    },
  },
} as const satisfies TranslationTree;
